/* eslint-disable require-jsdoc */
import React, { useState } from 'react';
import IconButton from '@mui/material/IconButton';
import { MoreVert as MoreVertIcon, Info as InfoIcon } from '@mui/icons-material';
import { styled } from '@mui/system';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu/Menu';
import MenuItem from '@mui/material/MenuItem/MenuItem';
import { Theme, Text } from '@simplybenefits/sb-ui-library';

const NotificationRow = styled('div')({
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 5,
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    height: 60,
    cursor: 'pointer',
    boxShadow: '0 1px 2px rgb(0 0 0 / 15%)',
    '&:hover': {
        filter: 'brightness(97%)'
    }
});

const STATUS_MAP = {
    'sent': {
        color: Theme.colors.skyBlue,
        text: 'Sent'
    },
    'failed': {
        color: Theme.colors.red,
        text: 'Failed'
    },
    'not_notified': {
        color: Theme.colors.mainTextGrey,
        text: 'Not Notified'
    }
};

const styles = {
    bodyTitle: {
        backgroundColor: Theme.colors.lightBlue
    },
    firstBlock: {
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        width: '15%',
        padding: '0 20px'
    },
    centerBlock: {
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        width: '12%',
        justifyContent: 'center'
    },
    emailBlock: {
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        width: '20%',
        justifyContent: 'center'
    },
    lastBlock: {
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        width: '12%',
        padding: '0 20px'
    },
    iconContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '5%',
        color: Theme.colors.mainTextGrey
    }
};

const truncate = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

const tooltipSlotProps = {
    tooltip: {
        sx: {
            backgroundColor: '#6D6E71',
            fontSize: '12px',
            fontFamily: 'Avenir'
        }
    }
};

const AlertIcon = ({ title }) => (
    <Tooltip title={ title } slotProps={ tooltipSlotProps }>
        <InfoIcon aria-label="info" style={{ fontSize: 15, color: Theme.colors.orange, marginLeft: '5px' }} />
    </Tooltip>
);

/**
 *
 * @param {*} param0
 * @returns
 */
const NotificationsRow = ({ row, onDownloadCsv }) => {
    const [ anchorEl, setAnchorEl ] = useState(null);

    const handleMenuClick = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const statusObj = STATUS_MAP[row.status] || STATUS_MAP.not_notified;

    return (
        <NotificationRow>
            <div style={{ display: 'flex', width: '95%', justifyContent: 'space-between' }}>
                <div style={{ ...styles.firstBlock, ...styles.bodyTitle }}>
                    <Text tiny customStyle={ truncate }>{ row.providerName }</Text>
                </div>
                <div style={ styles.emailBlock }>
                    <Text tiny customStyle={ truncate }>{ row.providerEmail }</Text>
                </div>
                <div style={ styles.centerBlock }>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2, overflow: 'hidden' }}>
                        <Text tiny customStyle={ truncate }>{ row.patientName }</Text>
                        {
                            row.isPatientNameMatch === false
                                ? <AlertIcon title="The patient name does not match the provided certificate or dependents." />
                                : null
                        }
                    </div>
                </div>
                <div style={ styles.centerBlock }>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2, overflow: 'hidden' }}>
                        <Text tiny customStyle={ truncate }>{ row.cert }</Text>
                        {
                            (row.userID === null && row.dependentID === null)
                                ? <AlertIcon title="There is no record of this certificate number." />
                                : null
                        }
                    </div>
                </div>
                <div style={ styles.centerBlock }>
                    <Text tiny customStyle={ truncate }>{ row.amountSubmitted }</Text>
                </div>
                <div style={ styles.centerBlock }>
                    <Text tiny customStyle={ truncate }>{ row.serviceDate }</Text>
                </div>
                <div style={ styles.centerBlock }>
                    <Text tiny customStyle={ truncate }>{ row.cdaUin }</Text>
                </div>
                <div style={ styles.centerBlock }>
                    <Text tiny customStyle={ truncate }>{ row.dateSent || '-' }</Text>
                </div>
                <div style={ styles.lastBlock }>
                    <Text bold tiny align="right" customStyle={{ ...truncate, color: statusObj.color }}>{ statusObj.text }</Text>
                    {
                        row.failureReason !== null
                            ? <AlertIcon title={ row.failureReason } />
                            : null
                    }
                </div>
            </div>

            <div style={ styles.iconContainer }>
                <IconButton
                    aria-label="Options"
                    onClick={ handleMenuClick }
                >
                    <MoreVertIcon />
                </IconButton>

                <Menu
                    anchorEl={ anchorEl }
                    open={ Boolean(anchorEl) }
                    onClose={ handleCloseMenu }
                    disableAutoFocusItem={ true }
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    <MenuItem onClick={ () => {
                        handleCloseMenu();
                        onDownloadCsv(row.type, row.batchID);
                    } }>
                        <Text color="inherit" medium display="inline">
                            Download CSV
                        </Text>
                    </MenuItem>
                </Menu>
            </div>
        </NotificationRow>
    );
};

export default NotificationsRow;